'use client';
import Link from "next/link";
import React, { useEffect, useState } from 'react';
import { translateHrefWithLocale } from "./translateHrefUtil.js";


function getCookie(name) {
    if (typeof document === 'undefined') return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
}

// normalize trailing slash (preserve root '/')
const normalizeBase = (p) => {
    if (!p) return '/';
    return (p.length > 1 && p.endsWith('/')) ? p.slice(0, -1) : p;
};

const LocalizedLinkClient = React.forwardRef(({ href, children, onClick, onNavigate, ...props }, ref) => {
    const [translatedPath, setTranslatedPath] = useState('');
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        setTranslatedPath(translateHrefWithLocale(href, getCookie('NEXT_LOCALE')));
    }, [href]);

    const scrollToHash = (hash) => {
        if (typeof document === 'undefined') return;
        const tryScroll = (attempt = 0) => {
            const el = document.getElementById(hash) || document.querySelector(`[name="${hash}"]`);
            if (el) {
                try {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } catch (_e) {
                    // ignore
                    window.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
                }
                return;
            }
            if (attempt < 6) {
                setTimeout(() => tryScroll(attempt + 1), 100);
            }
        };
        tryScroll();
    };

    const handleClick = (e) => {
        // Prefer onNavigate if provided (server forwarded prop), but call both if present.
        if (typeof onNavigate === 'function') {
            try { onNavigate(e); } catch (_err) { /* swallow */ }
        }

        if (onClick) {
            try { onClick(e); } catch (_err) { /* swallow */ }
        }

        if (!href || typeof window === 'undefined') return;

        const hashIndex = href.indexOf('#');
        if (hashIndex === -1) return; // let normal Link behavior handle non-hash links

        e.preventDefault();

        const hash = decodeURIComponent(href.substring(hashIndex + 1));

        // compute translated targets (use latest locale)
        const translatedFull = translateHrefWithLocale(href, getCookie('NEXT_LOCALE')) || href;
        const translatedBase = (translatedFull.split('#')[0]) || '/';

        const currentBase = window.location.pathname + (window.location.search || '');

        if (normalizeBase(translatedBase) === normalizeBase(currentBase)) {
            // same page: force scroll even if hash didn't change
            scrollToHash(hash);
            // also update location.hash so browser history reflects the anchor
            try {
                history.replaceState(null, '', `${translatedBase}#${encodeURIComponent(hash)}`);
            } catch (_ignore) { /* ignore */ }
        } else {
            // navigate to the target page. We avoid calling useRouter here because
            // that hook can throw if the Next router isn't mounted (e.g. in some test/storybook environments).
            // A full navigation is the safest option and the browser will handle scrolling to the hash after load.
            try {
                window.location.href = translatedFull;
            } catch (_err) {
                // fallback: set href directly
                window.location.assign(translatedFull);
            }
        }
    };

    // Avoid hydration mismatches when locale/cookies differ between server and client.
    if (!mounted) return null;

    return (
      <Link
        href={translatedPath || href}
        ref={ref}
        onNavigate={handleClick}
        suppressHydrationWarning
        {...props}
      >
        {children}
      </Link>
    );
});

export default LocalizedLinkClient;
